import { PRODUCT_RECORDS_PATH, PRODUCT_RECORDS_TABS } from "@/pages/analytics/product/productRecordsTabs";
import type { AnalyticsPlatform } from "@/types/analytics";

/**
 * Where the Platform control changes the numbers (ANL-E04-S07, report ch.6 change 2 "make Platform
 * truthful"). Pure data and functions: the filter bar reads the honour of the current path, and the
 * API client sends `platform` only to a route that declares it.
 */

export type PlatformOption = { value: AnalyticsPlatform; label: string };

export type PlatformHonour =
  | { honoured: true; note: string }
  | { honoured: false; reason: string };

/** Every stored platform, plus All (which filters none of them out). */
export const PLATFORM_OPTIONS: readonly PlatformOption[] = [
  { value: "all", label: "All" },
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" },
  { value: "web", label: "Web" },
  { value: "server", label: "Server" },
  { value: "email", label: "Email" },
];

/**
 * The admin analytics routes whose query declares `platform` (API-28 to API-33, the mobile DTO).
 * The core read, splits, dashboard and export ignore it, so they are never sent it (AC5).
 */
const PLATFORM_HONOURING_ROUTES: readonly string[] = [
  "/overview",
  "/retention",
  "/screens",
  "/events",
  "/referrals",
  "/event-types",
];

export const routeHonoursPlatform = (route: string): boolean => PLATFORM_HONOURING_ROUTES.includes(route);

// Proposed copy (03-ux/screens/product-records.md §4.3).
const MOBILE_HONOURS: PlatformHonour = {
  honoured: true,
  note: "All includes web, server and email events.",
};
const PRODUCT_RECORDS_IGNORE: PlatformHonour = {
  honoured: false,
  reason: "Platform does not apply here: this tab counts product records, not tracked events.",
};
/** A path outside the table: no view there reads tracked events by platform yet. */
const NOT_HONOURED: PlatformHonour = { honoured: false, reason: "Platform does not apply here." };

/** The per-view honour table: each Product records tab, keyed by its path. */
const PLATFORM_HONOUR: ReadonlyMap<string, PlatformHonour> = new Map(
  PRODUCT_RECORDS_TABS.map(({ slug }) => [
    `${PRODUCT_RECORDS_PATH}/${slug}`,
    slug === "mobile" ? MOBILE_HONOURS : PRODUCT_RECORDS_IGNORE,
  ]),
);

export const platformHonourForPath = (pathname: string): PlatformHonour => {
  for (const [path, honour] of PLATFORM_HONOUR) {
    if (pathname === path || pathname.startsWith(`${path}/`)) return honour;
  }
  return NOT_HONOURED;
};

/**
 * The platform the numbers on `pathname` are actually for: the address's choice where Platform is
 * honoured, otherwise All, so neither the disabled select nor the status strip names a platform
 * the tiles ignore.
 */
export const effectivePlatform = (pathname: string, platform: AnalyticsPlatform): AnalyticsPlatform =>
  platformHonourForPath(pathname).honoured ? platform : "all";

/**
 * The Mobile analytics info popover (AC6, proposed copy): the known adjacent defect, owned by
 * ANL-E05, until the web mirror stops stamping server facts as web.
 */
export const MOBILE_ANALYTICS_INFO =
  "Some server events are currently recorded as Web. Until the web mirror is fixed, Web can include server activity and Server can undercount it.";
